import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import TopographicCodesModal from '@/modules/configurationModule/detail/samples/topography/TopographicCodesModal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import { IContentTopographicCodesGrid, initialTopographicCodesGridState } from '@/modules/configurationModule/detail/samples/topography/TopographicCodes.models'
import { topographicCodesConfigurationServices } from '@/api/resources/configuration/samples/topography/TopographicCodesConfigurationServices'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        TopographicCodesModal,
        CodificationSearcherModal
    }
})
export default class TopographicCodesGrid extends BaseCrudServerSideComponent<IContentTopographicCodesGrid> implements BaseCrud {
    @Ref() readonly topographicCodesModalWrapper!: any
    @Ref() readonly codificationSearcherModalWrapper!: any

    constructor() {
        super()
        this.state = initialTopographicCodesGridState()
    }

    showCodificationSearcherModal() {
        this.codificationSearcherModalWrapper.show()
    }

    showTopographyModal(title:string | any, selectedTopography:IContentTopographicCodesGrid) {
        this.state.actionTitle = title
        this.topographicCodesModalWrapper.show([selectedTopography])
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:IContentTopographicCodesGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.topographicCodesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async createOperation(data: IContentTopographicCodesGrid) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(topographicCodesConfigurationServices.createSampleTopographies(data))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.topographicCodesModalWrapper.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(data: IContentTopographicCodesGrid[]) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(topographicCodesConfigurationServices.patchSamplesTopographiesBatch(data))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.topographicCodesModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: IContentTopographicCodesGrid[]) {
        const topographicCodesToPatch = selectionBatch?.filter((topography: IContentTopographicCodesGrid) => topography.active !== option)?.map((topography:IContentTopographicCodesGrid) => {
            return {
                ...topography,
                active: option
            }
        })
        if (topographicCodesToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(topographicCodesConfigurationServices.patchSamplesTopographiesBatch(topographicCodesToPatch))
            this.$_updateLoading(false)
            if (err) {
                this.$_showErrorModalFromErrorResponse(err)
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async deleteBatchOperation(topographies: IContentTopographicCodesGrid[]) {
        const topographicCodesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: topographies.map((topography: IContentTopographicCodesGrid) => Number(topography?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(topographicCodesConfigurationServices.deleteSampleTopographiesBatch(topographicCodesToDelete))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (topographies.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: topographies?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(topographies: IContentTopographicCodesGrid[]) {
        if (topographies.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this.deleteBatchOperation(topographies)
            }
        }
    }
}
